import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import Onion from "../maps/onion"
import MapPopup from "../maps/popup"
import Campus from "../maps/popups/campus"
import SearchResult from "../maps/popups/search_result"
import { useMeta } from 'stimulus-use'

export default class extends Controller {
  static targets = ['container', 'controlPanel', 'legend']
  static metaNames = ['mapsVersion']
  static values = {
    lat: Number,
    lng: Number,
    zoom: Number,
    bounds: Array
  }

  // useMeta defines non-configurable getters. initialize runs once per instance;
  // connect runs again when the element is disconnected and reconnected.
  initialize () {
    useMeta(this)
  }

  connect () {
    this.onion = undefined
    this.map = this.createMap()
    if (!this.map) {
      this.showMapUnavailable()
      return
    }

    this.onion = new Onion(this.map)
    this.onion.sourceVersion = this.mapsVersionMeta

    this.map.on('load', () => {
      this.setControls()
      this.moveControlPanel()
    })
  }

  disconnect () {
    this.map?.remove()
  }

  // Mapbox throws "Failed to initialize WebGL." when the canvas cannot get a context.
  createMap () {
    if (!mapboxgl.supported()) return

    try {
      return new mapboxgl.Map({
        container: this.containerTarget,
        style: this.mbStyle,
        center: [this.lngValue, this.latValue],
        zoom: this.zoomValue,
        bounds: this.boundsValue,
        antialias: true
      })
    } catch (error) {
      if (!this.webglInitFailure(error)) throw error
    }
  }

  webglInitFailure (error) {
    return typeof error?.message === 'string' && error.message.startsWith('Failed to initialize WebGL')
  }

  showMapUnavailable () {
    const notice = document.createElement('div')
    notice.dataset.mapUnavailable = 'true'
    notice.className = 'flex h-full items-center justify-center p-6 text-center text-lg text-slate-600'
    notice.setAttribute('role', 'status')
    notice.textContent = 'Unable to load map. Enable hardware acceleration or try a different browser'

    this.containerTarget.replaceChildren(notice)

    if (this.hasControlPanelTarget) {
      this.controlPanelTarget.classList.add('hidden')
    }
  }

  setControls () {
    this.map.addControl(this.scale)
    this.map.addControl(this.fullScreen)
    this.map.addControl(this.nav)
  }

  moveControlPanel () {
    this.containerTarget.appendChild(this.controlPanelTarget) // Fullscreen compatible
    this.controlPanelTarget.classList.remove('hidden')
  }

  flyTo (lng, lat, zoom) {
    this.map.flyTo({
      center: [lng, lat],
      zoom: zoom,
      speed: 0.5
    })
  }

  togglePins (event) {
    if (this.onion.hasLayer(event.params.layer)) {
      this.onion.toggleLayer(event.params.layer)
    } else {
      this.addPins(event.params.url)
    }
  }

  async addPins (url) {
    return fetch(url)
      .then(this.handleResponse)
      .then(json => this.onion.addLayerFromJson(json.config, json.data))
      .then(config => this.addPopup(config.popup, config.id))
  }

  addPopup (type, layerId) {
    let popup

    switch(type) {
      case 'Campus':
        popup = new Campus(this.map)
        break;
      case 'SearchResult':
        popup = new SearchResult(this.map)
        break;
      default:
        popup = new MapPopup(this.map)
        break;
    }

    popup.addTo(layerId)
  }

  handleResponse (response) {
    if (response.ok) {
      return response.json()
    } else {
      throw new Error(response.statusText)
    }
  }

  get mbStyle () {
    return 'mapbox://styles/mortarstone/cl22dpkji002815mytt2liioj'
  }

  get scale () {
    return new mapboxgl.ScaleControl({ unit: 'imperial' })
  }

  get fullScreen () {
    return new mapboxgl.FullscreenControl()
  }

  get nav () {
    return new mapboxgl.NavigationControl({ showZoom: true, showCompass: true })
  }
}
